import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StarRating } from "@/components/reviews/StarRating";
import { ReviewDetailModal } from "@/components/reviews/ReviewDetailModal";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { format } from "date-fns";
import { TrendingUp, MessageSquare, ArrowLeft, Building2, User, Eye, CheckCircle, Flag } from "lucide-react";
import { ReportReviewModal } from "@/components/reviews/ReportReviewModal";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

type ReviewType = "all" | "profile" | "company";

export default function ReviewsManage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileStats, setProfileStats] = useState<any>(null);
  const [companyStats, setCompanyStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("unanswered");
  const [typeFilter, setTypeFilter] = useState<ReviewType>("all");
  const [profileId, setProfileId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportReviewId, setReportReviewId] = useState<string | null>(null);
  const [reportReviewTitle, setReportReviewTitle] = useState<string>("");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadUserData();
    }
  }, [user]);

  useEffect(() => {
    if (profileId || companyId) {
      loadReviews();
      loadStats();
    }
  }, [profileId, companyId, activeTab, typeFilter]);

  const loadUserData = async () => {
    try {
      // Get profile ID
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user?.id)
        .single();

      if (profile) {
        setProfileId(profile.id);
      }

      // Get company ID if user owns a company
      const { data: company } = await supabase
        .from("companies")
        .select("id")
        .eq("owner_user_id", user?.id)
        .maybeSingle();

      if (company) {
        setCompanyId(company.id);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };

  const loadReviews = async () => {
    try {
      setLoading(true);
      let allReviews: any[] = [];

      // Load profile reviews if profileId exists and filter allows
      if (profileId && (typeFilter === "all" || typeFilter === "profile")) {
        let profileQuery = supabase
          .from("reviews")
          .select("*")
          .eq("profile_id", profileId)
          .order("created_at", { ascending: false });

        if (activeTab === "unanswered") {
          profileQuery = profileQuery.is("response_text", null);
        } else if (activeTab === "answered") {
          profileQuery = profileQuery.not("response_text", "is", null);
        }

        const { data: profileReviews } = await profileQuery;
        if (profileReviews) {
          allReviews = [...allReviews, ...profileReviews.map(r => ({ ...r, review_type: 'profile' }))];
        }
      }

      // Load company reviews if companyId exists and filter allows
      if (companyId && (typeFilter === "all" || typeFilter === "company")) {
        let companyQuery = supabase
          .from("reviews")
          .select("*")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false });

        if (activeTab === "unanswered") {
          companyQuery = companyQuery.is("response_text", null);
        } else if (activeTab === "answered") {
          companyQuery = companyQuery.not("response_text", "is", null);
        }

        const { data: companyReviews } = await companyQuery;
        if (companyReviews) {
          allReviews = [...allReviews, ...companyReviews.map(r => ({ ...r, review_type: 'company' }))];
        }
      }

      // Sort by created_at descending
      allReviews.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      // Load reviewer names in batch
      if (allReviews.length > 0) {
        const userIds = [...new Set(allReviews.map(r => r.user_id))];
        const { data: reviewerProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", userIds);

        // Map names to reviews
        const reviewerMap = new Map(reviewerProfiles?.map(p => [p.user_id, p]) || []);
        allReviews = allReviews.map(review => ({
          ...review,
          reviewer: reviewerMap.get(review.user_id) || null
        }));
      }

      setReviews(allReviews);
    } catch (error) {
      console.error("Error loading reviews:", error);
      toast.error(t("reviews.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Load profile stats
      if (profileId) {
        const { data: pStats } = await supabase.rpc("calculate_profile_rating", {
          p_profile_id: profileId,
        });
        if (pStats && pStats.length > 0) {
          setProfileStats(pStats[0]);
        }
      }

      // Load company stats
      if (companyId) {
        const { data: cStats } = await supabase.rpc("calculate_company_rating", {
          p_company_id: companyId,
        });
        if (cStats && cStats.length > 0) {
          setCompanyStats(cStats[0]);
        }
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleRespond = async (reviewId: string, responseText: string) => {
    if (!responseText.trim()) {
      toast.error(t("reviews.responseRequired"));
      return;
    }

    try {
      const { error } = await supabase
        .from("reviews")
        .update({
          response_text: responseText,
          response_date: new Date().toISOString(),
        })
        .eq("id", reviewId);

      if (error) throw error;

      toast.success(t("reviews.responseSuccess"));
      loadReviews();
    } catch (error) {
      console.error("Error responding to review:", error);
      toast.error(t("reviews.responseError"));
    }
  };

  const openReviewModal = (review: any) => {
    setSelectedReview(review);
    setIsModalOpen(true);
  };

  const closeReviewModal = () => {
    setSelectedReview(null);
    setIsModalOpen(false);
  };

  const openReportModal = (review: any) => {
    setReportReviewId(review.id);
    setReportReviewTitle(review.title);
    setIsReportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportReviewId(null);
    setReportReviewTitle("");
    setIsReportModalOpen(false);
  };

  const totalReviews = (profileStats?.total_reviews || 0) + (companyStats?.total_reviews || 0);
  const profileReviewCount = reviews.filter(r => r.review_type === 'profile').length;
  const companyReviewCount = reviews.filter(r => r.review_type === 'company').length;

  if (loading && !profileId && !companyId) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        {/* Breadcrumb Navigation */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/dashboard">
                {t("common.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("reviews.manageReviews")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back Button e Title */}
        <div className="flex items-center gap-4 mb-2">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <h1 className="font-heading text-3xl font-bold text-dark">
            {t("reviews.manageReviews")}
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-muted-foreground mb-8 ml-14">
          {t("reviews.manageDescription", "Gestisci e rispondi alle recensioni ricevute")}
        </p>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* Profile Stats */}
          <BrutalCard className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-muted-foreground">
                {t("reviews.profileRating", "Media Profilo")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold">
                {profileStats?.average_rating || "0.0"}
              </span>
              <StarRating rating={profileStats?.average_rating || 0} readonly size="sm" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {profileStats?.total_reviews || 0} {t("reviews.reviews", "recensioni")}
            </p>
          </BrutalCard>

          {/* Company Stats */}
          {companyId && (
            <BrutalCard className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-muted-foreground">
                  {t("reviews.companyRating", "Media Azienda")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold">
                  {companyStats?.average_rating || "0.0"}
                </span>
                <StarRating rating={companyStats?.average_rating || 0} readonly size="sm" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {companyStats?.total_reviews || 0} {t("reviews.reviews", "recensioni")}
              </p>
            </BrutalCard>
          )}

          <BrutalCard className="p-6">
            <div className="text-sm text-muted-foreground mb-1">
              {t("reviews.totalReviews")}
            </div>
            <div className="text-3xl font-bold">{totalReviews}</div>
          </BrutalCard>

          <BrutalCard className="p-6">
            <div className="text-sm text-muted-foreground mb-1">
              {t("reviews.responseRate", "Tasso risposte")}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold">
                {totalReviews > 0 
                  ? Math.round((reviews.filter(r => r.response_text).length / totalReviews) * 100)
                  : 0}%
              </span>
              <TrendingUp className="w-5 h-5 text-success" />
            </div>
          </BrutalCard>
        </div>

        {/* Type Filter - Only show if user has both profile and company */}
        {companyId && (
          <div className="mb-6">
            <label className="block text-sm font-bold mb-2">
              {t("reviews.filterByType", "Filtra per tipo")}
            </label>
            <BrutalSelect value={typeFilter} onValueChange={(value: ReviewType) => setTypeFilter(value)}>
              <BrutalSelectTrigger className="w-56">
                <BrutalSelectValue />
              </BrutalSelectTrigger>
              <BrutalSelectContent>
                <BrutalSelectItem value="all">
                  {t("reviews.allTypes", "Tutte le recensioni")}
                </BrutalSelectItem>
                <BrutalSelectItem value="profile">
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t("reviews.profileOnly", "Solo profilo")}
                  </span>
                </BrutalSelectItem>
                <BrutalSelectItem value="company">
                  <span className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {t("reviews.companyOnly", "Solo azienda")}
                  </span>
                </BrutalSelectItem>
              </BrutalSelectContent>
            </BrutalSelect>
          </div>
        )}

        {/* Reviews Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="unanswered">
              {t("reviews.unanswered")}
            </TabsTrigger>
            <TabsTrigger value="answered">
              {t("reviews.answered")}
            </TabsTrigger>
            <TabsTrigger value="all">{t("reviews.allReviews")}</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-6">
            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-32 bg-gray-200 rounded"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
              </div>
            ) : reviews.length === 0 ? (
              <BrutalCard className="p-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {t("reviews.noReviewsFound")}
                </p>
              </BrutalCard>
            ) : (
              reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-card border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left side - Review info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        {/* Type Badge */}
                        {review.review_type === "company" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold border-[2px] border-blue-700">
                            <Building2 className="w-3 h-3" />
                            {t("reviews.companyReview", "Azienda")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold border-[2px] border-amber-700">
                            <User className="w-3 h-3" />
                            {t("reviews.profileReview", "Profilo")}
                          </span>
                        )}
                        <StarRating rating={review.rating} readonly size="sm" />
                        <h3 className="font-heading text-lg font-bold text-dark truncate">
                          {review.title}
                        </h3>
                      </div>
                      
                      {/* Secondary info */}
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span>{format(new Date(review.created_at), "dd MMM yyyy")}</span>
                        <span className="hidden sm:inline">•</span>
                        <span className="truncate max-w-[250px] hidden sm:inline">
                          {review.comment}
                        </span>
                        {review.response_text && (
                          <>
                            <span>•</span>
                            <span className="text-success flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              {t("reviews.responded", "Risposto")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Right side - Action buttons */}
                    <div className="flex items-center gap-2 shrink-0">
                      <BrutalButton
                        variant="outline"
                        size="sm"
                        onClick={() => openReviewModal(review)}
                        title={t("reviews.viewDetails", "Visualizza dettagli")}
                      >
                        <Eye className="w-4 h-4" />
                      </BrutalButton>
                      <BrutalButton
                        variant="outline"
                        size="sm"
                        onClick={() => openReportModal(review)}
                        title={t("reviews.reportReview", "Segnala recensione")}
                        className="border-warning text-warning hover:bg-warning hover:text-white"
                      >
                        <Flag className="w-4 h-4" />
                      </BrutalButton>
                    </div>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Review Detail Modal */}
        <ReviewDetailModal
          review={selectedReview}
          isOpen={isModalOpen}
          onClose={closeReviewModal}
          onRespond={handleRespond}
        />

        {/* Report Review Modal */}
        <ReportReviewModal
          reviewId={reportReviewId || ""}
          reviewTitle={reportReviewTitle}
          isOpen={isReportModalOpen}
          onClose={closeReportModal}
        />
      </main>
      
      <Footer />
    </div>
  );
}
